import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';

interface FileUploadListViewProps {
    /** Combined list of files to render (uploaded + locally selected). */
    files: any[];
    /** Show the "FILES AND ATTACHMENTS" header above the list. */
    showHeader?: boolean;
    isDisableDelete?: boolean;
    isDisableDownload?: boolean;
    /** Open the image preview dialog (images only). */
    onPreview: (file: any) => void;
    /** Download an uploaded file / open a locally selected one. */
    onOpen: (file: any) => void;
    onDelete: (file: any) => void;
}

/**
 * Presentational vertical list of attachments (icon + name + delete/download),
 * used by FileUpload when `listView` is set. Clicking a row previews images and
 * opens/downloads other file types.
 */
export const FileUploadListView: React.FC<FileUploadListViewProps> = ({
    files,
    showHeader = false,
    isDisableDelete,
    isDisableDownload,
    onPreview,
    onOpen,
    onDelete,
}) => {
    const handleRowClick = (file: any) => {
        // Only images have a meaningful preview; other file types open/download
        // instead of showing a broken image dialog.
        if (file.type?.startsWith('image/')) {
            onPreview(file);
        } else if (!isDisableDownload) {
            onOpen(file);
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            {showHeader && (
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <DescriptionIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" color="primary.main">
                        FILES AND ATTACHMENTS
                    </Typography>
                </Box>
            )}
            {files.map((file, index) => (
                <Box
                    key={file.file_id ?? file.file_uuid ?? file.name ?? index}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    px={1.5}
                    py={1}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        cursor: 'pointer',
                    }}
                    onClick={() => handleRowClick(file)}
                >
                    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                        {file.type === 'application/pdf' ? (
                            <PictureAsPdfIcon fontSize="small" color="error" />
                        ) : (
                            <DescriptionIcon fontSize="small" color="action" />
                        )}
                        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                            {file.name}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                        {file.uploading && (
                            <Typography variant="caption" color="text.secondary">
                                Uploading...
                            </Typography>
                        )}
                        {!isDisableDelete && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(file);
                                }}
                            >
                                <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                        )}
                        {!isDisableDownload && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpen(file);
                                }}
                            >
                                <FileDownloadIcon fontSize="small" color="primary" />
                            </IconButton>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};
